<div class="custom-form flex flex-col gap-4">
  @if (loading() || !viewMode() || data().briefingNote?.note) {
    @if (!hideMessageSubtitle()) {
      <div class="flex flex-col">
        <span [style.fontWeight]="subtitleFontWeight()" [style.fontSize.px]="subtitleFontSize()">
          {{
            viewMode()
              ? ('components.briefingNotes.message' | translate)
              : data().creatorName
                ? ('components.briefingNotes.messageToCreator' | translate: { creatorName: data().creatorName })
                : ('components.briefingNotes.messageToCreators' | translate)
          }}
        </span>
      </div>
    }

    <div class="flex flex-col w-full">
      <div id="briefingNotesToolbar" class="briefing-notes-toolbar" [class.!hidden]="viewMode() || loading()">
        <span class="ql-formats">
          <button
            class="ql-bold"
            matTooltipPosition="above"
            [matTooltipShowDelay]="500"
            [matTooltip]="'components.quillEditor.toolbar.bold' | translate"
          ></button>
          <button
            class="ql-list"
            value="ordered"
            matTooltipPosition="above"
            [matTooltipShowDelay]="500"
            [matTooltip]="'components.quillEditor.toolbar.orderedList' | translate"
          ></button>
          <button
            class="ql-list"
            value="bullet"
            matTooltipPosition="above"
            [matTooltipShowDelay]="500"
            [matTooltip]="'components.quillEditor.toolbar.bulletList' | translate"
          ></button>
          <button
            class="ql-link"
            matTooltipPosition="above"
            [matTooltipShowDelay]="500"
            [matTooltip]="'components.quillEditor.toolbar.link' | translate"
          ></button>
          <button
            mat-flat-button
            type="button"
            color="secondary-transparent"
            class="hover:!bg-transparent mat-tiny-button"
            [matMenuTriggerFor]="moreFormattingMenu"
            (click)="rememberSelection()"
          >
            <div class="flex items-center justify-center">
              {{ 'shared.more' | translate }}
            </div>
          </button>
        </span>
      </div>

      <mat-menu #moreFormattingMenu="matMenu">
        <button mat-menu-item type="button" (click)="applyFormat('header', 1)">
          {{ 'components.quillEditor.toolbar.heading1' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('header', 2)">
          {{ 'components.quillEditor.toolbar.heading2' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('header', 3)">
          {{ 'components.quillEditor.toolbar.heading3' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('header', false)">
          {{ 'components.quillEditor.toolbar.normalText' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('italic', true)">
          {{ 'components.quillEditor.toolbar.italic' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('underline', true)">
          {{ 'components.quillEditor.toolbar.underline' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('strike', true)">
          {{ 'components.quillEditor.toolbar.strike' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('blockquote', true)">
          {{ 'components.quillEditor.toolbar.quote' | translate }}
        </button>
        <button mat-menu-item type="button" (click)="applyFormat('code-block', true)">
          {{ 'components.quillEditor.toolbar.codeBlock' | translate }}
        </button>
      </mat-menu>

      <div
        class="briefing-notes-editor-wrapper"
        [class.briefing-notes-view-expandable]="viewMode() && enableTextShowMore() && hasOverflow() && !loading()"
        [class.briefing-notes-view-collapsed]="
          viewMode() && enableTextShowMore() && !isNoteExpanded() && hasOverflow() && !loading()
        "
        [style.max-height]="
          viewMode() && enableTextShowMore() && hasOverflow()
            ? isNoteExpanded()
              ? noteContentHeight() + 'px'
              : collapsedHeight + 'px'
            : null
        "
        #noteEditorWrapper
      >
        @if (loading()) {
          <ngx-skeleton-loader
            class="block"
            [style.height]="enableTextShowMore() ? collapsedHeight + 'px' : '242px'"
            [theme]="{
              'border-radius': viewMode() ? '8px' : '0 0 8px 8px',
              height: enableTextShowMore() ? collapsedHeight + 'px' : '242px',
              width: '100%',
            }"
          />

          <div class="h-[5.5px] w-full"></div>
        } @else {
          <quill-editor
            #quillEditor
            class="custom-quill-editor briefing-notes-editor w-full"
            [class.ql-rounded-top-corners]="viewMode()"
            [class.ql-text-show-more]="viewMode() && enableTextShowMore()"
            [readOnly]="viewMode()"
            [formControl]="noteControl"
            [modules]="quillModules"
            [placeholder]="
              viewMode() && noteControl.value === null
                ? ('components.briefingNotes.noMessage' | translate)
                : ('components.briefingNotes.messagePlaceholder' | translate)
            "
            (onEditorCreated)="onEditorCreated()"
          />

          @if (!enableTextShowMore()) {
            <span class="briefing-notes-resize-indicator"></span>
          }
        }
      </div>

      @if (!viewMode() && !loading()) {
        <div class="flex items-center justify-between">
          <span class="text-shikenso-error-form-field text-[10px]">
            @if (noteControl.errors?.['textTooLong']) {
              {{ 'components.briefingNotes.errors.textTooLong' | translate }}
            } @else if (noteControl.errors?.['htmlTooLarge']) {
              {{ 'components.briefingNotes.errors.htmlTooLarge' | translate }}
            }
          </span>

          <span
            class="text-[10px]"
            [class.text-shikenso-purple-base-60]="textLengthPercent() < 80"
            [class.text-shikenso-warning-base]="textLengthPercent() >= 80 && textLengthPercent() < 100"
            [class.text-shikenso-error-form-field]="textLengthPercent() >= 100"
          >
            {{ currentTextLength() }} / {{ maxTextLength }}
          </span>
        </div>
      }

      @if (viewMode() && enableTextShowMore() && hasOverflow() && !loading()) {
        <div
          class="flex justify-center relative z-10 briefing-notes-toggle-btn-wrapper"
          [class.briefing-notes-toggle-btn-wrapper--collapsed]="!isNoteExpanded()"
        >
          <button
            mat-flat-button
            type="button"
            color="secondary-gray-border"
            class="mat-micro-button"
            (click)="toggleExpanded()"
          >
            <div class="flex items-center gap-1">
              <span>
                {{
                  (isNoteExpanded() ? 'components.briefingNotes.showLess' : 'components.briefingNotes.showMore')
                    | translate
                }}
              </span>

              @if (isNoteExpanded()) {
                <app-icon src="icons/arrow-up.svg" [size]="12" />
              } @else {
                <app-icon src="icons/arrow-down.svg" [size]="12" />
              }
            </div>
          </button>
        </div>
      }
    </div>
  }

  @if (loading() || !viewMode() || data().briefingNote?.files?.length) {
    <div class="flex flex-col gap-2 w-full mt-1">
      <div class="flex items-center gap-1 mb-1">
        <span [style.fontWeight]="subtitleFontWeight()" [style.fontSize.px]="subtitleFontSize()">
          {{ 'components.briefingNotes.attachments' | translate }}
        </span>

        @if (!viewMode()) {
          <span class="text-shikenso-purple-base-60 text-sm">
            ({{ 'components.briefingNotes.optional' | translate }})
          </span>
        }
      </div>

      @if (!viewMode()) {
        <app-file-uploader
          size="big"
          [imageWidth]="0"
          [imageHeight]="0"
          [title]="'components.briefingNotes.fileUploadTitle' | translate"
          [fileForm]="uploadFileControl"
          [fileExtensions]="[
            'csv',
            'doc',
            'docx',
            'gif',
            'jpeg',
            'jpg',
            'webp',
            'pdf',
            'png',
            'ppt',
            'pptx',
            'txt',
            'xls',
            'xlsx',
          ]"
          [clearAfterUpload]="true"
          (imageSelected)="onUploadSelected($event)"
        />
      }

      @if (loading()) {
        <div class="flex flex-col gap-2">
          @for (index of listUploaderSkeletonIndexes; track index) {
            <ngx-skeleton-loader class="h-7" [theme]="{ 'border-radius': '8px', height: '28px', width: '100%' }" />
          }
        </div>
      } @else if (files().length) {
        <div class="flex flex-col">
          @for (file of files(); track file.id ?? file.fileId) {
            <app-file-uploader
              [allowDocumentUploads]="true"
              [displayMode]="'list'"
              [imageId]="file.fileId"
              [isViewMode]="viewMode()"
              (imageRemoved)="removeFile(file)"
            />
          }
        </div>
      } @else if (viewMode() && !files().length) {
        <span class="text-shikenso-purple-base-60 text-sm">
          {{ 'components.briefingNotes.noAttachments' | translate }}
        </span>
      }
    </div>
  }

  @if (!viewMode()) {
    <div class="flex items-center justify-end gap-3 mt-2">
      <button mat-flat-button color="secondary-transparent" (click)="cancelled.emit()">
        {{ 'shared.cancel' | translate }}
      </button>

      <button mat-flat-button [disabled]="noteControl.invalid" (click)="onSaveClick()">
        {{ 'pages.campaignForm.ctaDialog.done' | translate }}
      </button>
    </div>
  }
</div>
